/**
 * Applies client config SEO values to the document head (title, meta description, Open Graph, Twitter Card, favicon).
 * Run once when config is available so search engines and social crawlers see the correct meta.
 */
import { useEffect } from 'react';
import { getPageTitle, getPageDescription, getOgImage, getSiteUrl, getFaviconUrl } from '../services/clientConfigService';

/** No visible pixels — not a tenant logo. Inline so the tab does not flash grey before config. */
export const TRANSPARENT_TAB_ICON_SVG_DATA_URL =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg'/%3E";

/** 1×1 transparent PNG; some browsers prefer a non-SVG `alternate icon`. */
export const TRANSPARENT_TAB_ICON_PNG_DATA_URL =
  'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAAC0lEQVR42mNgAAIAAAUAAen63NgAAAAASUVORK5CYII=';

/** Firestore `updatedAt` on client config — used to bust aggressive browser favicon caches after admin saves. */
function getClientConfigUpdatedRev(): string {
  if (typeof window === 'undefined') return '';
  const cfg = (window as unknown as { __CLIENT_CONFIG?: { updatedAt?: unknown } }).__CLIENT_CONFIG;
  const u = cfg?.updatedAt;
  if (u == null) return '';
  if (typeof u === 'object' && u !== null && 'seconds' in u) {
    return String((u as { seconds: number }).seconds);
  }
  if (typeof u === 'object' && u !== null && typeof (u as { toMillis?: () => number }).toMillis === 'function') {
    return String((u as { toMillis: () => number }).toMillis());
  }
  return '';
}

function withFaviconCacheBust(url: string): string {
  let rev = getClientConfigUpdatedRev();
  if (!rev) {
    let h = 0;
    for (let i = 0; i < url.length; i += 1) {
      h += url.charCodeAt(i);
    }
    rev = String(h % 1_000_000);
  }
  const sep = url.includes('?') ? '&' : '?';
  return `${url}${sep}siteIconRev=${encodeURIComponent(rev)}`;
}

const TAB_ICON_RELS = ['icon', 'shortcut icon', 'apple-touch-icon', 'alternate icon'] as const;

function removeAllTabIconLinks(): void {
  TAB_ICON_RELS.forEach((rel) => {
    document.querySelectorAll(`link[rel="${rel}"]`).forEach((el) => el.remove());
  });
}

export function syncDocumentHeadFromConfig() {
  const title = getPageTitle();
  const description = getPageDescription();
  const ogImage = getOgImage();
  const faviconUrl = getFaviconUrl();
  const currentUrl = typeof window !== 'undefined' ? window.location.href : getSiteUrl();

  if (title) document.title = title;

  const ensureMeta = (attr: string, content: string, isProperty = false) => {
    const key = isProperty ? 'property' : 'name';
    let el = document.querySelector(`meta[${key}="${attr}"]`) as HTMLMetaElement | null;
    if (!el) {
      el = document.createElement('meta');
      el.setAttribute(key, attr);
      document.head.appendChild(el);
    }
    el.setAttribute('content', content);
  };

  if (description) ensureMeta('description', description);
  if (title) ensureMeta('og:title', title, true);
  if (description) ensureMeta('og:description', description, true);
  if (ogImage) ensureMeta('og:image', ogImage, true);
  ensureMeta('og:url', currentUrl, true);
  ensureMeta('og:type', 'website', true);
  ensureMeta('twitter:card', 'summary_large_image');
  if (title) ensureMeta('twitter:title', title);
  if (description) ensureMeta('twitter:description', description);
  if (ogImage) ensureMeta('twitter:image', ogImage);

  /**
   * Tab icon: `branding.favicon` when set; else CMC attribution logo when `poweredBy.show` and `poweredBy.logoUrl` resolve.
   * Otherwise transparent data-URL placeholders (not a logo) so there is no grey flash and browsers are less likely to hit `/favicon.ico` SPA rewrites.
   */
  removeAllTabIconLinks();

  if (faviconUrl) {
    const href = withFaviconCacheBust(faviconUrl);
    const icon = document.createElement('link');
    icon.rel = 'icon';
    icon.href = href;
    const lower = faviconUrl.split('?')[0].toLowerCase();
    if (lower.endsWith('.svg') || lower.includes('.svg?')) {
      icon.type = 'image/svg+xml';
      icon.setAttribute('sizes', 'any');
    } else if (lower.endsWith('.png') || lower.includes('.png?')) {
      icon.type = 'image/png';
      icon.setAttribute('sizes', '192x192');
    } else if (lower.endsWith('.webp') || lower.includes('.webp?')) {
      icon.type = 'image/webp';
      icon.setAttribute('sizes', '192x192');
    } else if (lower.endsWith('.jpg') || lower.endsWith('.jpeg') || lower.includes('.jpg?') || lower.includes('.jpeg?')) {
      icon.type = 'image/jpeg';
      icon.setAttribute('sizes', '192x192');
    } else if (lower.endsWith('.ico') || lower.includes('.ico?')) {
      icon.type = 'image/x-icon';
      icon.setAttribute('sizes', '48x48');
    }
    document.head.appendChild(icon);

    const apple = document.createElement('link');
    apple.rel = 'apple-touch-icon';
    apple.href = href;
    apple.setAttribute('sizes', '180x180');
    document.head.appendChild(apple);
  } else {
    const svgIcon = document.createElement('link');
    svgIcon.rel = 'icon';
    svgIcon.type = 'image/svg+xml';
    svgIcon.setAttribute('sizes', 'any');
    svgIcon.href = TRANSPARENT_TAB_ICON_SVG_DATA_URL;
    document.head.appendChild(svgIcon);
    const altIcon = document.createElement('link');
    altIcon.rel = 'alternate icon';
    altIcon.type = 'image/png';
    altIcon.setAttribute('sizes', '1x1');
    altIcon.href = TRANSPARENT_TAB_ICON_PNG_DATA_URL;
    document.head.appendChild(altIcon);
  }
}

export function SeoHead() {
  useEffect(() => {
    syncDocumentHeadFromConfig();
    const onNavStyleChange = () => syncDocumentHeadFromConfig();
    window.addEventListener('navigation-style-changed', onNavStyleChange);
    return () => window.removeEventListener('navigation-style-changed', onNavStyleChange);
  }, []);

  return null;
}
